export enum JobTypes {
  Command = "Command",
  AIML = "AIML",
  Abaqus = "Abaqus",
  Cfx = "CFX",
  Starccm = "STAR-CCM",
  Fluent = "Fluent",
  Mechanical = "Mechanical",
  Lsdyna = "LS-DYNA",
  Converge = "Converge",
  Actran = "MSC-Actran",
  Nastran = "MSC-Nastran",
  Optistruct = "OptiStruct",
  Optis = "Optis",
  Radioss = "Radioss",
  Hfss = "HFSS",
  Fluidx = "NanoFluidX",
  DynamicJob = "DynamicJob",
  OpenFoam = "OpenFOAM",
}

export enum Entities {
  Home = "Home",
  Dashboard = "Dashboard",
  Deployments = "Deployments",
  Reports = "Reports",
  Nodes = "Nodes",
  Jobs = "Jobs",
  ML = "MLOps",
  Storages = "Storages",
  Users = "Users",
  Applications = "Applications",
  Configurator = "Configurator",
  Gateway = "Gateway",
  LicenseServer = "Licenses",
  Sessions = "Sessions",
  ClientSession = "Client Sessions",
  AIStudio = "AI Studio"
}
export enum ProjectEntity {
  NODES = 'Nodes',
  PODS = 'Pods',
  JOBS = 'Jobs',
  REPOSITORY = 'Repository',
  DATASETS = 'Datasets',
  STORAGES = 'Storages',
  AGENTS = 'Agents',
  MODELS = 'Models'
}

export interface EntityConfig {
  key: ProjectEntity;
  label: string;
  icon: string;
  description: string;
}

export const PROJECT_ENTITIES: EntityConfig[] = [
  {
    key: ProjectEntity.NODES,
    label: 'Nodes',
    icon: 'bi-diagram-3',
    description: 'Manage compute nodes and resources'
  },
  {
    key: ProjectEntity.PODS,
    label: 'Pods',
    icon: 'bi-boxes',
    description: 'Container orchestration and management'
  },
  {
    key: ProjectEntity.JOBS,
    label: 'Jobs',
    icon: 'bi-play-circle',
    description: 'Task execution and job scheduling'
  },
  {
    key: ProjectEntity.REPOSITORY,
    label: 'Repository',
    icon: 'bi-git',
    description: 'Source code and version control'
  },
  {
    key: ProjectEntity.DATASETS,
    label: 'Datasets',
    icon: 'bi-database',
    description: 'Data management and storage'
  },
  {
    key: ProjectEntity.STORAGES,
    label: 'Storages',
    icon: 'bi-hdd-stack',
    description: 'File system and storage management'
  },
  {
    key: ProjectEntity.AGENTS,
    label: 'Agents',
    description: 'AI agents and automation',
    icon: 'bi-robot'
  },
  {
    key: ProjectEntity.MODELS,
    label: 'Models',
    description: 'Machine learning models',
    icon: 'bi-cpu'
  }
];

export interface Dataset {
  datasetId: string;
  projectId: string;
  name: string;
  version: string;
  description: string;
  createdBy: string;
  createdAt: Date;
  modifiedAt: Date;
  category: string;
  tags: string;
  fileType: string;
  accessLevel: string;
  size: number;
  entries: number;
  downloads: number;
  trainSplit: number;
  validationSplit: number;
  testSplit: number;
  metadata: string;
  codeSnippet: string;
  isOwner: boolean;
}

export interface CreateDatasetRequest {
  projectId: string;
  name: string;
  version: string;
  description: string;
  createdBy: string;
  category: string;
  tags: string;
  fileType: string;
  accessLevel: string;
  size: number;
  entries: number;
  trainSplit: number;
  validationSplit: number;
  testSplit: number;
  metadata: string;
  codeSnippet: string;
}

export interface DatasetListResponse {
  datasets: Dataset[];
  totalCount: number;
}

export interface DatasetStatsResponse {
  categoryStats: { [key: string]: number };
  fileTypeStats: { [key: string]: number };
  totalDatasets: number;
  totalSize: number;
  totalDownloads: number;
}

export enum SubEntities {
  UserGroups = "Groups",
  UserAssignments = "Assignments",
  ConfiguratorOverview = "Overview",
  UserMonitor = "Monitor",
}

export enum Graphs {
  JobSummary = "JobSummary",
  JobCompleted = "JobCompleted",
  JobDataCount = "JobDataCount",
  JobByApplication = "JobByApplication",
  JobByNodes = "JobByNodes",
  JobByNodeGroup = "JobByNodeGroup",
  JobWaitTime = "JobWaitTime",
  JobByUser = "JobByUser",
  JobByGroup = "JobByGroup",
  StorageUsage = "StorageUsage",
  CoresByApplication = "CoresByApplication",
  CoreHoursByUsers = "CoreHoursByUsers",
  CoreHoursByGroups = "CoreHoursByGroups",
  CoreUsage = "CoreUsage",
  NodeAvailability = "NodeAvailability",
  NodeUtilization = "NodeUtilization",
  LicenseUsage = "LicenseUsage",
  SingleNodeUtilization = "SingleNodeUtilization",
  SessionGraph = "SessionGraph",
  PrimeNonPrimeCoreUsage = "PrimeNonPrimeCoreUsage",
  JobByTags = "JobByTags",
  ActiveSessions = "ActiveSessions",
  RAMUtilization = "RAMUtilization",
  CPUUtilization = "CPUUtilization",
  GpuMemoryUtilization = "GpuMemoryUtilization",
  NetworkUtilization = "NetworkUtilization",
  AllUserSessions = "AllUserSessions",
  AllUserSessionsPerHost = "AllUserSessionsPerHost",
  NodeStartStopStatus = "NodeStartStopStatus",
  IndividualNodeNetworkUtilization = "IndividualNodeNetworkUtilization",
  NodeUtilizationByNodeGroup = "NodeUtilizationByNodeGroup",
  HpcUnitsConsumed = "HpcUnitsConsumed",
  CpuUsageByNodes = "CpuUsageByNodes",
  CpuUsageByNodeGroups = "CpuUsageByNodeGroups",
  CoreUsageByNodes = "CoreUsageByNodes",
  CoreUsageByNodeGroups = "CoreUsageByNodeGroups",
  JobDurationsByDay = "JobDurationsByDay",
  JobByTagsPerDay = "JobByTagsPerDay",
  RunningQueuedJobsPerNodeGroup = "RunningQueuedJobsPerNodeGroup",
  PrimeNonPrimeJobSubmission = "PrimeNonPrimeJobSubmission",
  JobsByUserByDay = "JobsByUserByDay"
}
  
export enum HpcVMStates {
  STATE_CODE_UNKNOWN = "Wait..",  //Billing OFF
  STATE_CODE_CREATING = "Provisioning..",  //Billing ON
  STATE_CODE_STARTING = "Starting..",  //Billing ON
  STATE_CODE_STARTED = "Active",   //Billing ON
  STATE_CODE_STOPING = "Stopping..",  //Billing ON  
  STATE_CODE_STOPING_CANCEL_WAIT = "Stopping...",  //wait state till all jobs not get canceled
  STATE_CODE_STOPPED = "Off (Allocated)",   //Billing ON  
  STATE_CODE_STOPPED_DEALLOC = "Inactive (Deallocated)", //Billing OFF
  STATE_CODE_DELETING = "Deleting",  //Billing OFF
  STATE_CODE_FAILED = "Failed",    //Billing OFF
  STATE_CODE_DEPLOYING = "Deploying (wait..)",  //Billing ON
  STATE_CODE_PRESTARTED = "Initializing (wait..)",
  STATE_CODE_UPDATING = "Updating",
  STATE_CODE_ACTIVE_OFFLINE = "Active (Offline)",
  STATE_CODE_UNASSIGNED = "Unassigned"
}

export enum HpcJobStageNames {
  STAGE_JOB_UNKNOWN = "Unknown",
  STAGE_JOB_QUEUED = "Job Queue",
  STAGE_JOB_INIT = "Job Init",
  STAGE_DATA_COPY = "Data Copy",
  STAGE_JOB_SUBMIT = "Job Submit",
  STAGE_JOB_EXECUTE = "Job Execute",
  STAGE_JOB_RESET_SCHEDULER = "Reset Workspace"
}

export enum OperationStatus {
  All = "All",
  Cancelled = "Cancelled",
  Cancelling = "Cancelling",
  CancellationError = "Cancellation Failed (Access Denied)",
  ContactSupport = "Contact Support",
  Failed = "Failed",
  LicenseQueued = "Waiting For License",
  LicenseUnreachable = "License Unreachable",
  PasswordIncorrect = "Failed (Access Denied)",
  Queued = "Queued",
  Running = "Running",
  Succeeded = "Succeeded",
  TimedOut = "Timed Out",
  Wait = "Wait"
}

export enum NodeTypes {
  TxtHeadNode = "Head",
  TxtComputeNode = "Compute",
  TxtLicenseServer = "License Server",
  TxtVPNServer = "VPN Server",
  TxtWorkstation = "Workstation",
  TxtWorkstore = "Workstore",
  TxtComputeAndWorkstore = "Compute + Workstore",
  TxtHead_Compute_Workstore = "Head + Compute + Workstore",
  TxtHead_Workstore = "Head + Workstore",
  TxtGpuCompute = "Gpu + Compute",
  TxtGpuMaster = "Gpu Master",
  TxtGpuSlave = "Gpu Slave",
  TxtHwHost = "Host (HW)",
  TxtStorage = "Storage",
  TxtSecondaryHeadNode = "Secondary Head Node"
}

export enum CloudTypes {
  MicrosoftAzure = "Microsoft Azure",
  OnPremise = "On-Premise",
  Oracle = "Oracle Cloud Infrastructure",
  Google = "Google Cloud Platform",
  KVM = "KVM Virtualization",
  AWS = "Amazon Web Services"
}

export enum CloudProviderTypes {
  'Amazon Web Services' = 0,
  'Microsoft Azure' = 2,
  'Microsoft Azure : VDI' = 3,
  'Google Cloud Platform' = 4,
  'KVM Virtualization' = 6,
  'Oracle Cloud Infrastructure' = 7,
  'On-Premise' = 9,
  'On-Premise : VDI' = 10,
  'Microsoft Azure : AI/ML' = 11,
}

export interface KeyValuePair {
  key: string,
  value: string
}

export enum Store {
  SyncStore = "SyncStore",
  WorkStore = "WorkStore"
}

export enum LicenseServerTypes {
  FlexLM,
  Altair,
  LsDyna,
  RLM,
  LMX,
  DSLS,
  Other
}

export interface NameValueObject {
  name: string,
  value: string
}

export enum ImportCSVType {
  Users = "Users",
  Nodes = "Nodes",
  Jobs = "Jobs"
}

export enum PlatformTypes {
  Windows = "WINDOWS",
  Linux = "LINUX"
}

export enum UploadFileTypes {
  ExecuteFile = "Execute File",
  LicenseFile = "License File",
  CancelFile = "Cancel File",
  ResumeFile = "Resume File",
  SuspendFile = "Suspend File",
  CustomActionFile = "Custom Action File",
}

export enum SyncHpcXML{
  UseVncFile = "false"
}

export enum AuthType {
  SyncHPCAPI = 0,
  LDAP = 1,
  AzureAD = 2,
  Okta = 3,
  OpenId = 4,
  SamlId = 5,
  PingIdentity = 6
}

export enum ResourceAvailability {
  AVAILABLE = "Available",
  FREE = "Free",
  BUSY = "Busy",
  IDLE = "Idle",
  UNAVAILABLE = "NA"
}

export enum GraphDataGranularity {
  OneHour = "One Hour",
  OneDay = "One Day",
  OneWeek = "One Week",
  OneMonth = "One Month"
}

export enum ErrorCodes {
  Cancelled,
  Conflict,
  Failed,
  In_Transition,
  Insufficient_Balance,
  Invalid,
  Not_Acceptable,
  Not_Found,
  Not_Implemented,
  Succeeded,
  Timed_Out,
  Unauthenticated,
  Unauthorized,
  Valid
}

export enum PeroformNodeAction {
  TxtStart = "Start", //1
  TxtStop = "Stop", //2
  TxtConnect = "Connect", //3
  TxtRestart = "Restart", //4
  TxtAttach = "Attach", //5
  TxtDetach = "Detach", //6
  TxtRemove = "Remove Session", //7
  TxtReports = "Reports", //8
  TxtTerminal = "Terminal", //9
  TxtEdit = "Edit", //10
  TxtDelete = "Delete", //11
  TxtAttachvGPU = "Attach vGPU", //12
  TxtDetachvGPU = "Detach vGPU", //13
  TxtAttachChildNodes = "Attach Childs", //14
  TxtUnmapUser = "Unmap User", //15
  TxtResizeVm = "Resize Node", //15
  TxtOpenConsole = "Console" //16
}

/*{ name: "Start", value: 1, title: "Start node", icon: "bi-caret-right", disabled: false },
{ name: "Stop", value: 2, title: "Stop node", icon: "bi-stop", disabled: false },
{ name: "Connect", value: 3, title: "Connect Node", icon: "bi-link", disabled: true },
{ name: "Restart", value: 4, title: "Restart selected node", icon: "bi-bootstrap-reboot", disabled: false },
{ name: "Attach", value: 5, title: "Attach workstation to deployment", icon: "bi-node-plus", disabled: true },
{ name: "Detach", value: 6, title: "Detach workstation from deployment", icon: "bi-input-cursor", disabled: true },
{ name: "Remove Session", value: 7, title: "Remove on-going sessions", icon: "bi-box-arrow-right", disabled: true },
{ name: "Reports", value: 8, title: "View reports of node", icon: "bi-graph-up", disabled: true },
{ name: "Terminal", value: 9, title: "Open terminal", icon: "bi-terminal", disabled: true },
{ name: "Edit", value: 10, title: "Edit Workstation Details", icon: "bi bi-pencil-square", disabled: true },
{ name: "Delete", value: 11, title: "Delete Node", icon: "bi bi-x-lg", disabled: true }*/

export interface SyncHpcAPIAuth {
  identityServer: string,
  identityServerPort: string
}

export interface LDAPAuth {
  enterpriseADServer: string,
  enterpriseDomainName: string
}

export interface AzureAuth {
  clientID: string,
  authority: string,
  redirectUri: string,
  cacheLocation: string,
  storeAuthStateInCookie?: boolean,
  protectedResourceMap: string,
  azureADMetadataEndpoint: string,
  azureADClientSecret: string,
  azureADTenantID: string
}

export interface OKTAAuth {
  issuer: string,
  clientId: string,
  redirectUri: string,
  scopes: string | string[],
  oktaMetadataEndpoint: string
}

export interface OpenIdAuth {
  domain: string,
  clientId: string,
  audience: string,
  clientSecret: string,
}
export interface SamlAuth {
  saml2: saml;
}

export interface saml {
  idPMetadata: string;
  issuer: string;
  signatureAlgorithm: string;
  certificateValidationMode: string;
  revocationMode: string;
}

export interface PingIdentityAuth {
  publicClientId: string;
  confidentialClientId: string;
  confidentialClientSecret: string;
  issuer: string;
  tokenEndpoint: string;
  redirectUri: string;
  introspectionEndpoint: string;
  discoveryEndpoint: string;
  scope: string;
  responseType: string;
  showDebugInformation: boolean;
  disablePKCE: boolean;
}


export enum ListRetrieveInterval {
  JobIntervalValue = 100,
  LogIntervalValue = 2000
}

export enum Granularity {
  OneHour = "One Hour",
  OneDay = "One Day",
  OneWeek = "One Week",
  OneMonth = "One Month"
}

/* When any new variable is added, then add in class of DashboardModulesE in Global.cs 
 */
export enum DashboardModulesE {
  Session = "Sessions",
  Nodes = "Nodes",
  NodesThreshold = "Nodes Crossing Threshold",
  Users = "Users",
  Deployment = "Deployments",
  Jobs = "Jobs",
  Storage = "Storages",
  License = "Licenses",
  SessionsByOS = "Sessions By OS",
  SessionsByDeployments = "Sessions By Deployments",
  AvailableResources = "Available Resources",
  Cores="Cores"
}

export enum SlurmPartitionDefaultValues {
  AllowGroups = 'ALL',
  AllowAccounts = 'ALL',
  AllowQos = 'ALL',
  AllocNodes = 'ALL',
  QoS = 'N/A',
  DefaultTime = 'NONE',
  DisableRootJobs = 'NO',
  ExclusiveUser = 'NO',
  GraceTime = '0',
  Hidden = 'NO',
  MaxNodes = 'UNLIMITED',
  MaxTime = 'UNLIMITED',
  MinNodes = '0',
  LLN = 'NO',
  MaxCPUsPerNode = 'UNLIMITED',
  PriorityJobFactor = '1',
  PriorityTier = '1',
  RootOnly = 'NO',
  ReqResv = 'NO',
  OverSubscribe = 'NO',
  OverTimeLimit = 'NONE',
  PreemptMode = 'OFF',
  State = 'UP',
  SelectTypeParameters = 'NONE',
  JobDefaults = '',
  DefMemPerNode = 'UNLIMITED',
  MaxMemPerNode = 'UNLIMITED'
}


export enum SlurmPartitionAdditionalPropsDescription {
  AllowGroups = 'Comma-separated list of group names which may execute jobs in this node group',
  AllowAccounts = 'Comma-separated list of accounts which may execute jobs in the node group. The default value is "ALL".',
  AllowQos = 'Comma-separated list of Qos which may execute jobs in the node group. Jobs executed as user root can use any node group without regard to the value of AllowQos. The default value is "ALL".',
  AllocNodes = 'Comma-separated list of nodes from which users can submit jobs in the node group. Node names may be specified using the node range expression syntax described above. The default value is "ALL".',
  QoS = 'Used to extend the limits available to a QOS on a node group. Jobs will not be associated to this QOS outside of being associated to the node group. They will still be associated to their requested QOS. By default, no QOS is used.',
  DefaultTime = 'Run time limit used for jobs that don\'t specify a value.If not set then MaxTime will be used. The default value is "NONE".',
  DisableRootJobs = 'If set to "YES" then user root will be prevented from running any jobs on this node group. The default value is "NO"',
  ExclusiveUser = 'If set to "YES" then nodes will be exclusively allocated to users. Multiple jobs may be run for the same user, but only one user can be active at a time. The default value is "NO"',
  GraceTime = 'Specifies, in units of seconds, the preemption grace time to be extended to a job which has been selected for preemption. The default value is zero, no preemption grace time is allowed on this node group. Once a job has been selected for preemption, its end time is set to the current time plus GraceTime.',
  Hidden = 'Specifies if the node group and its jobs are to be hidden by default. Hidden node groups will by default not be reported by the Slurm APIs or commands. Possible values are "YES" and "NO". The default value is "NO".',
  MaxNodes = 'Maximum count of nodes which may be allocated to any single job. The default value is "UNLIMITED", which is represented internally as -1.',
  MaxTime = 'Maximum run time limit for jobs. Format is minutes, minutes:seconds, hours:minutes:seconds, days-hours, days-hours:minutes, days-hours:minutes:seconds or "UNLIMITED".',
  MinNodes = 'Minimum count of nodes which may be allocated to any single job. The default value is 0.',
  LLN = 'Schedule resources to jobs on the least loaded nodes (based upon the number of idle CPUs). The default value is "NO"',
  MaxCPUsPerNode = 'Maximum number of CPUs on any node available to all jobs from this node group. The default value is "UNLIMITED"',
  Nodes = '',
  PriorityJobFactor = 'node group factor used by priority/multifactor plugin in calculating job priority. The value may not exceed 65533. The default value is 1',
  PriorityTier = 'Jobs submitted to a node group with a higher PriorityTier value will be evaluated by the scheduler before pending jobs in a node group with a lower PriorityTier value. The default value is 1',
  RootOnly = 'Specifies if only user ID zero (i.e. user root) may allocate resources in this node group. User root may allocate resources for any other user, but the request must be initiated by user root. This option can be useful for a node group to be managed by some external entity (e.g. a higher-level job manager) and prevents users from directly using those resources. Possible values are "YES" and "NO". The default value is "NO"',
  ReqResv = 'Specifies users of this node group are required to designate a reservation when submitting a job. This option can be useful in restricting usage of a node group that may have higher priority or additional resources to be allowed only within a reservation. Possible values are "YES" and "NO". The default value is "NO".',
  OverSubscribe = 'Controls the ability of the node group to execute more than one job at a time on each resource. The default value is "NO"',
  OverTimeLimit = 'Number of minutes by which a job can exceed its time limit before being canceled. May not exceed 65533 minutes. The default value is "NONE"',
  PreemptMode = 'OFF',
  State = 'State of node group or availability for use. Possible values are "UP", "DOWN", "DRAIN" and "INACTIVE". The default value is "UP".',
  SelectTypeParameters = 'node group-specific resource allocation type. This option replaces the global SelectTypeParameters value. Supported values are CR_Core, CR_Core_Memory, CR_Socket and CR_Socket_Memory. The default value is "NONE"',
  JobDefaults = '',
  DefMemPerNode = 'Default real memory size available per allocated node in megabytes. Used to avoid over-subscribing memory and causing paging. The default value is "UNLIMITED"',
  MaxMemPerNode = 'Maximum real memory size available per allocated node in megabytes. Used to avoid over-subscribing memory and causing paging. The default value is "UNLIMITED"'
}

export interface HostDevice {
    name: string,
    id: string,
    description: string,
    driver: string
}

export enum NodeProvisionSource {
  TxtReferenceDisk = "Reference Disk", //1
  TxtTemplate = "Template", //2
  //TxtISO = "ISO", //3
}

export enum AccessLevels 
{
    Private = 0,
    Deployment = 1,
    Global = 2
}
export enum ProjectStatus 
{
    Active = 1,
    InActive = 2,
    Completed = 3,
    Abandoned = 4,
    }

export class FileTypes{
  static TxtFile = "TXT"; //1 - '.txt', '.log', '.csv', '.tsv', '.json', '.xml', '.dat'
  static ImageFile = "IMAGE"; //2  - '.png', '.jpg', '.jpeg', '.gif', '.bmp', 'svg', 'webp', 'avif'
  static VideoFile = "VIDEO"; //3  - '.mp4','.webm','.mkv','.mov','.ogv' 
  static ModelViewerFile = "MODEL_VIEWER"; //4  - '.gltf', '.glb'
  static AudioFile = "AUDIO"; //5  - 'mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma'

  static checkFileType(path){
		if(this.checkIfImage(path)){
			return this.ImageFile;
		}
		else if(this.checkIfVideo(path)){
			return this.VideoFile;
		}
		else if(this.checkIfModelViewer(path)){
			return this.ModelViewerFile;
		}
		return this.TxtFile;
	}

	static checkIfImage(path){
		const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', 'svg', 'webp', 'avif', 'ico'];
		for (const ext of imageExtensions) {
			if (path.endsWith(ext)) {
				return true;
			}
		}
		return false;
	}

	static checkIfVideo(path){
		const videoExtensions = ['.mp4','.webm','.mkv','.mov','.ogv'];
		for (const ext of videoExtensions) {
			if (path.endsWith(ext)) {
				return true;
			}
		}
		return false;
	}

	static checkIfModelViewer(path){
		const modelViewerExtensions = ['.gltf', '.glb'];
		for (const ext of modelViewerExtensions) {
			if (path.endsWith(ext)) {
				return true;
			}
		}
		return false;
	}

  static checkIfText(path: string, TextFileExtension: string[] = []): boolean {
    if(path.includes('.zip'))
      return false; // Exclude zip files from text file check

    const allItems = [
      '.sh', '.txt', '.log', '.csv', '.tsv', '.json', '.xml', '.dat', '.env', 
      '.py', '.yaml', '.yml', '.md', '.html', '.css', '.js', '.ts', '.cpp',
      '.c', '.h',
      ...TextFileExtension
    ];
    
    const fileName = path.split('/').pop();
    const hasExtension = fileName.includes('.') && !fileName.startsWith('.');
    const lowerFileName = fileName.toLowerCase();

    if(hasExtension) {
      // Check only items that start with dot (extensions)
      return allItems
        .filter(item => item.startsWith('.'))
        .some(ext => lowerFileName.endsWith(ext.toLowerCase()));
    } else {
      if (TextFileExtension.includes("*")) {
        return true;
      }
      // Check only items that don't start with dot (filenames)
      return allItems
        .filter(item => !item.startsWith('.'))
        .some(name => lowerFileName === name.toLowerCase());
    }
  }

  static checkIfAudio(path: string): boolean {
    return ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma'].some(ext => path.toLowerCase().endsWith(ext));
  }

  static getMimeTypeForBlob(path: string, TextFileExtension: string[] = []): string {
    const lowerPath = path.toLowerCase();
  
    const mimeTypes: { [key: string]: string } = {
      //#region [Video]
      '.mp4': 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"',
      '.webm': 'video/webm',
      '.mkv': 'video/x-matroska',
      '.mov': 'video/quicktime',
      '.ogv': 'video/ogg',
      //#endregion [Video]
  
      //#region [Audio]
      '.mp3': 'audio/mpeg',
      '.wav': 'audio/wav',
      '.ogg': 'audio/ogg',
      '.flac': 'audio/flac',
      '.aac': 'audio/aac',
      '.m4a': 'audio/mp4',
      '.wma': 'audio/x-ms-wma',
      //#endregion [Audio]
  
      //#region [Image]
      '.png': 'image/png',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.gif': 'image/gif',
      '.bmp': 'image/bmp',
      '.svg': 'image/svg+xml',
      '.webp': 'image/webp',
      '.avif': 'image/avif',
      //#endregion [Image]

      //#region [3D Models]
      '.gltf': 'model/gltf+json',
      '.glb': 'model/gltf-binary',
      //#endregion [3D Models]
  
      //#region [Text/Log/Code/CSV/etc.]
      '.sh': 'application/x-sh',
      '.txt': 'text/plain',
      '.log': 'text/plain',
      '.csv': 'text/csv',
      '.tsv': 'text/tab-separated-values',
      '.json': 'application/json',
      '.xml': 'application/xml',
      '.dat': 'application/octet-stream',
      '.env': 'text/plain',
      '.py': 'text/x-python',
      '.yaml': 'text/yaml',
      '.yml': 'text/yaml',
      '.md': 'text/markdown',
      '.html': 'text/html',
      '.css': 'text/css',
      '.js': 'text/javascript',
      '.ts': 'text/typescript',
      '.cpp': 'text/x-c++src',
      '.c': 'text/x-csrc',
      '.h': 'text/x-chdr',
      //#endregion [Text/Log/Code/CSV/etc.]
    };
  
    for (const ext in mimeTypes) {
      if (lowerPath.endsWith(ext)) {
        return mimeTypes[ext];
      }
    }

    if (this.checkIfText(path, TextFileExtension)) {
      return 'text/plain';
    }
  
    return 'application/octet-stream';
  }
  
}
